import type { StickyChatTarget } from './sticky-chat-target';

/**
 * What a tap on the resting sticky "Ask {maestro}" bar does (`StickyChatBar`).
 *
 *  - `inline` — the bar grows into its own input-only composer, which hands off on send. Always on narrow
 *    (the phones' rule, and there is no side to open on), and on wide while `symphonyStickyChatSideChatWeb`
 *    is off.
 *  - `side` — the side chat dock opens with its input focused: the screen's own chat when it has one (the
 *    task page), the Calendar's thread on the Calendar (`calendarConversationId`), else the last
 *    conversation (`lastConversationId`). `conversationId: null` means the caller has no last conversation
 *    in hand yet; the bar then looks up the most recent one, and opens a fresh chat only when there is none.
 *
 * A task with no chat yet stays `inline` even with the flag on: its send carries the `taskId` reference,
 * which an empty dock cannot, so opening the dock there would quietly drop it.
 */
export type StickyChatTap = { kind: 'inline' } | { kind: 'side'; conversationId: string | null; taskId?: string };

export function stickyChatTap({
  narrow,
  sideChatEnabled,
  conversationScope,
  target,
  lastConversationId = null,
  calendarConversationId = null,
}: {
  narrow: boolean;
  sideChatEnabled: boolean;
  conversationScope: 'new' | 'calendar';
  target: StickyChatTarget | null;
  lastConversationId?: string | null;
  calendarConversationId?: string | null;
}): StickyChatTap {
  if (narrow || !sideChatEnabled) return { kind: 'inline' };
  if (conversationScope === 'calendar') return { kind: 'side', conversationId: calendarConversationId ?? lastConversationId };
  if (target?.conversationId) {
    return { kind: 'side', conversationId: target.conversationId, ...(target.taskId ? { taskId: target.taskId } : {}) };
  }
  if (target?.taskId) return { kind: 'inline' };
  return { kind: 'side', conversationId: lastConversationId };
}
